import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import type { PriceId, ProductKind } from '@iheartjane/types';

import { WEIGHT_TO_GRAMS } from '@jane/bloom/dataAccess/constants';
import type { CartItem } from '@jane/bloom/types/schemas/bloom/checkout';
import type {
  ProductApplicableIds,
  StoreSpecial,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { CustomRowRules } from '@jane/bloom/types/schemas/roots/bloom/customRow';
import { formatWeight } from '@jane/bloom/utils/strings/formatWeight';
import type { SpecialsViewModel } from '@jane/bloom/viewModels/specials';
import type { AlgoliaProduct } from '@jane/search/types';

const isSoldByWeight = (productKind: ProductKind) => {
  return ['flower', 'extract', 'vape'].includes(productKind);
};

const parsePrice = (value: unknown): number | null => {
  if (typeof value === 'string') return parseFloat(value);
  if (typeof value === 'number') return value;
  return null;
};

const productInCart = (
  cartProducts: CartItem[],
  productId: number,
  priceId?: string
) => {
  return cartProducts?.some(
    (cartProduct) =>
      cartProduct.productId === productId &&
      (!priceId || cartProduct.priceId === priceId)
  );
};

const getPrice = (
  product: SmartSortProduct<AlgoliaProduct>,
  weightId: PriceId
) => {
  const priceKey = `price_${weightId}` as keyof AlgoliaProduct;
  const discountedPriceKey =
    `discounted_price_${weightId}` as keyof AlgoliaProduct;
  const rawDiscountedPrice = product.attributes[discountedPriceKey];

  return {
    discountedPrice: parsePrice(rawDiscountedPrice),
    price: parsePrice(product.attributes[priceKey]),
    rawDiscountedPrice,
  };
};

const passesPriceThreshold = (
  price: number,
  threshold?: { maximum_price?: number | null; minimum_price?: number | null }
) => {
  if (!threshold) return true;
  const { maximum_price, minimum_price } = threshold;
  if (minimum_price != null && price < minimum_price) return false;
  if (maximum_price != null && price > maximum_price) return false;
  return true;
};

const rulesIncludeWeight = (
  includesWeights: string[],
  excludesWeights: string[],
  weightId: PriceId
) => {
  let allowedWeights = includesWeights;
  if (excludesWeights.length) {
    allowedWeights = allowedWeights.filter(
      (weight) => !excludesWeights.includes(weight)
    );
  }

  if (allowedWeights.length && !allowedWeights.includes(weightId)) {
    return false;
  }

  return true;
};

type ShouldShowWeightProps = {
  cartItems: CartItem[];
  currentSpecial: StoreSpecial;
  showOnlyWeights?: ProductApplicableIds[] | null;
  specialsProducts: SpecialsViewModel;
};

export type ShouldShowWeightFilter = (
  weight: string,
  product: SmartSortProduct<AlgoliaProduct>
) => boolean;

/**
 * Returns a filter function that decides whether a weight should be shown for a
 * product on a special detail page. Order of checks (mirrors monolith):
 *   1. Cart bypass — a weight already in the user's cart is always shown.
 *   2. showOnlyWeights — restricts to API-supplied applicable price IDs.
 *   3. Price validity — drop weights with no price or a discounted price of 0.
 *   4. Special-type-specific rules (bulk/bundle/spending_threshold/product/cart_total).
 */
export const shouldShowWeight = ({
  cartItems,
  currentSpecial,
  showOnlyWeights,
  specialsProducts,
}: ShouldShowWeightProps): ShouldShowWeightFilter => {
  const allowedWeightIds = showOnlyWeights?.length
    ? new Set(showOnlyWeights.map(formatWeight))
    : null;

  return (weight: string, product: SmartSortProduct<AlgoliaProduct>) => {
    const productKind = product.attributes['kind'] as ProductKind;

    if (!isSoldByWeight(productKind)) return true;

    const productId = product.attributes['product_id'];
    const weightId = formatWeight(weight) as PriceId;

    if (productInCart(cartItems, productId, weightId)) return true;

    if (allowedWeightIds && !allowedWeightIds.has(weightId)) return false;

    const { discountedPrice, price, rawDiscountedPrice } = getPrice(
      product,
      weightId
    );

    if (
      (rawDiscountedPrice != null && discountedPrice === 0) ||
      price == null ||
      isNaN(price)
    ) {
      return false;
    }

    const isUnlocked = !!specialsProducts?.isUnlocked;

    if (currentSpecial.special_type === 'bundle') {
      // Bundle rules carry qualifier/discountable as arrays of sections
      const section = isUnlocked
        ? currentSpecial.rules?.discountable?.[0]
        : currentSpecial.rules?.qualifier?.[0];
      const includesWeights = section?.includes?.[0]?.weights || [];
      const excludesWeights = section?.excludes?.[0]?.weights || [];

      return rulesIncludeWeight(includesWeights, excludesWeights, weightId);
    }

    if (currentSpecial.special_type === 'spending_threshold') {
      const rules = isUnlocked
        ? currentSpecial.rules.discountable
        : currentSpecial.rules.qualifier;
      const includesWeights = rules?.includes?.[0]?.weights || [];
      const excludesWeights = rules?.excludes?.[0]?.weights || [];

      return rulesIncludeWeight(includesWeights, excludesWeights, weightId);
    }

    if (
      currentSpecial.special_type === 'product' ||
      currentSpecial.special_type === 'cart_total'
    ) {
      const includesWeights = currentSpecial.rules.includes?.[0]?.weights || [];
      const excludesWeights = currentSpecial.rules.excludes?.[0]?.weights || [];

      return rulesIncludeWeight(includesWeights, excludesWeights, weightId);
    }

    if (currentSpecial.special_type !== 'bulk_pricing') return true;

    // Bulk pricing: target_weight remaining capacity, then bulk_weights match + price thresholds.
    const cartWeight = specialsProducts?.currentWeight ?? 0;
    const targetWeight = specialsProducts?.targetWeight;
    if (targetWeight && WEIGHT_TO_GRAMS[weightId] > targetWeight - cartWeight) {
      return false;
    }

    const bulkWeights = currentSpecial.rules?.includes?.[0]?.bulk_weights || [];
    if (!bulkWeights.length) return true;

    const weightCondition = bulkWeights.find((w) => w.price_id === weightId);
    if (!weightCondition) return false;

    return passesPriceThreshold(price, weightCondition);
  };
};

export type ProductFilterFunction = (
  products: SmartSortProduct<AlgoliaProduct>[]
) => SmartSortProduct<AlgoliaProduct>[];

/**
 * Drops products from a bulk-pricing deal grid when no available weight survives
 * the weight filter. Reuses the caller's `weightFilter` so the gate logic stays
 * defined in one place (`shouldShowWeight`) and isn't run twice per render.
 *
 * Returns undefined for non-bulk_pricing specials so the grid renders untouched.
 *
 * Note: Since filtering is done on the frontend after receiving data from the
 * backend, the total product count reported by the backend may be inaccurate as
 * it won't reflect products filtered out by weight conditions. Long term the DM
 * SDK should handle this.
 */
export function createBulkDealProductFilter(
  currentSpecial: StoreSpecial,
  weightFilter: ShouldShowWeightFilter
): ProductFilterFunction | undefined {
  if (currentSpecial.special_type !== 'bulk_pricing') {
    return undefined;
  }

  return (products: SmartSortProduct<AlgoliaProduct>[]) =>
    products.filter((product) =>
      (product.attributes['available_weights'] || []).some((weight: string) =>
        weightFilter(weight, product)
      )
    );
}

/** Creates a weight filter for custom rows with price threshold rules. */
export function createCustomRowWeightFilter(
  rules?: CustomRowRules
): ShouldShowWeightFilter | undefined {
  const productThreshold = rules?.product_threshold;
  if (!productThreshold) return undefined;

  const { maximum_price, minimum_price } = productThreshold;
  if (minimum_price == null && maximum_price == null) return undefined;

  return (weight: string, product: SmartSortProduct<AlgoliaProduct>) => {
    const productKind = product.attributes['kind'];
    if (!isSoldByWeight(productKind)) return true;

    const priceKey = `price_${formatWeight(weight)}` as keyof AlgoliaProduct;
    const price = parsePrice(product.attributes[priceKey]);

    if (price == null || isNaN(price)) return false;
    return passesPriceThreshold(price, productThreshold);
  };
}
